<template>
  <!--
    A turn's status line above its answer. While Oliver works it names the step it is on
    and keeps a clock; once the answer starts it folds to "Worked for 1m 12s", which opens
    the steps it took.
  -->
  <div v-if="visible" class="reasoning">
    <div
      v-if="working"
      class="reasoning__working"
      role="status"
      aria-live="polite"
    >
      <status-spinner :size="15" />
      <span class="reasoning__text">
        <shimmer-text class="reasoning__line">{{ line }}</shimmer-text>
        <span v-if="clock" class="reasoning__clock" aria-hidden="true">{{
          clock
        }}</span>
      </span>
    </div>

    <template v-else>
      <button
        type="button"
        class="reasoning__worked"
        :aria-expanded="String(expanded)"
        :aria-controls="bodyId"
        @click="expanded = !expanded"
      >
        <span>{{ line }}</span>
        <svg
          width="11"
          height="11"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2.5"
          stroke-linecap="round"
          stroke-linejoin="round"
          class="reasoning__chevron"
          aria-hidden="true"
        >
          <path :d="expanded ? 'M6 9l6 6 6-6' : 'M9 6l6 6-6 6'" />
        </svg>
      </button>

      <collapse-transition>
        <div v-if="expanded" :id="bodyId" class="reasoning__steps">
          <activity-step-list :steps="steps" :done="finished" />
        </div>
      </collapse-transition>
    </template>
  </div>
</template>

<script>
import ActivityStepList from "@/components/OliverBotV2/chat/ActivityStepList.vue";
import CollapseTransition from "@/components/OliverBotV2/shared/CollapseTransition.vue";
import ShimmerText from "@/components/OliverBotV2/shared/ShimmerText.vue";
import StatusSpinner from "@/components/OliverBotV2/shared/StatusSpinner.vue";
import {
  finishedWork,
  formatElapsed,
  isWorking,
  workedLine,
  workingLine,
} from "@/components/OliverBotV2/utils/reasoningSummary";

let blockCount = 0;

export default {
  name: "ReasoningBlock",
  components: {
    ActivityStepList,
    CollapseTransition,
    ShimmerText,
    StatusSpinner,
  },
  props: {
    /** The turn's activity: { steps, startedAt, durationMs, done, phase, outcome }. */
    activity: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      bodyId: `reasoning-steps-${++blockCount}`,
      expanded: false,
      /** The clock the elapsed time is read against, ticking only while Oliver works. */
      now: Date.now(),
    };
  },
  computed: {
    steps() {
      return this.activity?.steps || [];
    },
    working() {
      return isWorking(this.activity);
    },
    finished() {
      return finishedWork(this.activity);
    },
    line() {
      return this.working
        ? workingLine(this.activity)
        : workedLine(this.activity);
    },
    clock() {
      const startedAt = this.activity?.startedAt;
      return this.working && startedAt
        ? formatElapsed(this.now - startedAt)
        : "";
    },
    /** Once the answer starts, a line with no steps to open says nothing the answer does not. */
    visible() {
      return Boolean(this.activity) && (this.working || this.steps.length > 0);
    },
  },
  watch: {
    working: {
      immediate: true,
      handler(on) {
        clearInterval(this._clock);
        this._clock = null;
        if (!on) return;
        this.now = Date.now();
        this._clock = setInterval(() => (this.now = Date.now()), 1000);
      },
    },
  },
  beforeUnmount() {
    clearInterval(this._clock);
  },
};
</script>

<style lang="scss" scoped>
// Level with the middle of Oliver's avatar beside it.
.reasoning {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding-top: 20px;
  margin-bottom: 12px;
  width: 100%;
}

.reasoning__working {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 15px;
  line-height: 1.4;
}

// The line and its clock sit on one baseline; the spinner centres on them.
.reasoning__text {
  display: flex;
  align-items: baseline;
  gap: 10px;
  min-width: 0;
}

.reasoning__line {
  min-width: 0;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.reasoning__clock {
  flex-shrink: 0;
  font-size: 13px;
  color: var(--oliver-text-muted);
  opacity: 0.75;
  font-variant-numeric: tabular-nums;
}

.reasoning__worked {
  display: inline-flex;
  align-self: flex-start;
  align-items: center;
  gap: 6px;
  padding: 0;
  border: 0;
  background: none;
  font-family: inherit;
  font-size: 14px;
  color: var(--oliver-text-muted);
  opacity: 0.85;
  cursor: pointer;
  transition: opacity 0.15s ease;

  &:hover {
    opacity: 1;
  }
}

.reasoning__chevron {
  opacity: 0.7;
}

.reasoning__steps {
  margin: 2px 0 4px 2px;
}
</style>
